import { useState } from 'react';
import { Link } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import { Cookie } from 'lucide-react';
import { Button } from './ui/button';
import { useLinks } from '@/lib/party';

const KEY = 'teaplus-cookie-consent';
export type CookieChoice = 'all' | 'essential';

export function readCookieChoice(): CookieChoice | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'all' || v === 'essential' ? v : null;
  } catch {
    return null;
  }
}

/**
 * The cookie notice for the browser version of Tea+ (web.teaplus.tv). The desktop app doesn't
 * show it. Tea+ only sets essential cookies today; the choice is kept so anything optional added
 * later respects it.
 */
export function CookieConsent() {
  const { features } = useLinks();
  const web = !window.teaplus?.desktop && !features.party;
  const [choice, setChoice] = useState(readCookieChoice);
  const choose = (c: CookieChoice) => {
    try {
      localStorage.setItem(KEY, c);
    } catch {
      // Storage blocked: the banner simply shows again next visit.
    }
    setChoice(c);
  };

  return (
    <AnimatePresence>
      {web && !choice && (
        <motion.div
          role="region"
          aria-label="Cookie notice"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-3 bottom-24 z-[70] mx-auto max-w-3xl lg:bottom-6"
        >
          <div className="relative overflow-hidden rounded-3xl border border-pink/25 bg-bg-2/95 p-5 shadow-[0_30px_80px_-24px_rgba(0,0,0,.9)] backdrop-blur-xl sm:p-6">
            <div
              aria-hidden
              className="pointer-events-none absolute -left-16 -top-24 h-56 w-56 rounded-full bg-pink/15 blur-3xl"
            />
            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-pink/15 text-pink">
                <Cookie className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Cookies, the essential kind</p>
                <p className="mt-1 text-sm text-muted">
                  Tea+ uses cookies to keep you signed in and remember your settings. No ads, no
                  tracking.{' '}
                  <Link to="/privacy" className="font-medium text-pink hover:underline">
                    Cookie &amp; Privacy Policy
                  </Link>
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button variant="secondary" onClick={() => choose('essential')}>
                  Essential only
                </Button>
                <Button onClick={() => choose('all')}>Accept</Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
